import { FC, createContext, useContext, useState } from 'react';
import noop from 'utils/noop';

type Value = {
  items: any[];
  filterFn: (item: any, query: string) => boolean;
  query: string;
  setQuery: (query: string) => void;
};

const defaultValues: Value = {
  items: [],
  filterFn: () => true,
  query: '',
  setQuery: noop,
};

export const ListContext = createContext(defaultValues);

type ListContextProviderProps = {
  items: any[];
  filterFn?: (item: any, query: string) => boolean;
};

type Item = {
  name: string;
  id: number;
};

const defaultFilterFn = (item: Item, query: string) =>
  item.name.toLowerCase().includes(query.toLowerCase());

/**
 * @deprecated This version of list is deprecated. A new version of list will be available in the future under ui/list.
 * @see https://aurora.iterable.com/latest/components/compositions/list/design-zGujCdjL on design guidance
 */
export const ListContextProvider: FC<React.PropsWithChildren<ListContextProviderProps>> = ({
  children,
  items,
  filterFn = defaultFilterFn,
}) => {
  const [query, setQuery] = useState('');

  return (
    <ListContext.Provider
      value={{
        items,
        filterFn,
        query,
        setQuery,
      }}
    >
      {children}
    </ListContext.Provider>
  );
};

export const useListContext = () => {
  const context = useContext(ListContext);

  if (context === undefined)
    throw new Error('useListContext must be used within a ListContextProvider');

  const { query, setQuery, items, filterFn } = context;

  const filteredItems = items.filter((item) => filterFn(item, query));
  const currentCount = filteredItems.length;
  const totalCount = items.length;

  return {
    query,
    setQuery,
    currentCount,
    totalCount,
    items,
    filteredItems,
  };
};
